import DescriptionIcon from "@mui/icons-material/Description";
import ErrorIcon from "@mui/icons-material/Error";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PersonIcon from "@mui/icons-material/Person";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import CircularProgress from "@mui/joy/CircularProgress";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import * as mammoth from "mammoth";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Document, DocumentId, DocumentWithDetails } from "../../../models/document";
import { Person } from "../../../models/person";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import {
  DocumentQuartzClientContext,
  PersonQuartzClientContext,
} from "../../../shared/services/api/contexts";

interface DocumentViewerProps {
  documentId: DocumentId;
}

interface ViewerState {
  document: Document | null;
  patient: Person | null;
  documentDetails: DocumentWithDetails | null;
  loading: boolean;
  loadingPatient: boolean;
  loadingDocumentDetails: boolean;
  error: string | null;
  docxHtml: string | null;
  convertingDocx: boolean;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({ documentId }) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const documentQuartzClient = React.useContext(DocumentQuartzClientContext);
  const personQuartzClient = React.useContext(PersonQuartzClientContext);
  const [state, setState] = React.useState<ViewerState>({
    document: null,
    patient: null,
    documentDetails: null,
    loading: true,
    loadingPatient: false,
    loadingDocumentDetails: false,
    error: null,
    docxHtml: null,
    convertingDocx: false,
  });

  React.useEffect(() => {
    const loadDocument = async () => {
      setState((prev) => ({
        ...prev,
        document: null,
        documentDetails: null,
        loading: true,
        error: null,
        docxHtml: null,
        convertingDocx: false,
      }));

      try {
        // Load basic document data
        const document = await documentQuartzClient.getDocument(documentId);
        setState((prev) => ({ ...prev, document, loading: false, error: null }));

        // Load enhanced document details with patient and note information
        setState((prev) => ({ ...prev, loadingDocumentDetails: true }));
        try {
          const documentDetails = await documentQuartzClient.getDocumentWithDetails(documentId);
          setState((prev) => ({ ...prev, documentDetails, loadingDocumentDetails: false }));
        } catch (detailsError: any) {
          console.warn("Failed to load document details:", detailsError);
          setState((prev) => ({ ...prev, documentDetails: null, loadingDocumentDetails: false }));
        }

        // Load patient information if document has a person_id
        if (document.metadata.person_id) {
          setState((prev) => ({ ...prev, loadingPatient: true }));
          try {
            const patient = await personQuartzClient.getPersonById(document.metadata.person_id);
            setState((prev) => ({ ...prev, patient, loadingPatient: false }));
          } catch (patientError: any) {
            console.warn("Failed to load patient:", patientError);
            setState((prev) => ({ ...prev, patient: null, loadingPatient: false }));
          }
        }
      } catch (error: any) {
        setState((prev) => ({
          ...prev,
          document: null,
          loading: false,
          error: error.message || "Failed to load document",
        }));
        showSnackbar({ text: error.message, color: "danger" });
      }
    };

    if (documentId) {
      loadDocument();
    }
  }, [documentId, showSnackbar, documentQuartzClient, personQuartzClient]);

  // Navigation functions
  const handlePatientClick = () => {
    if (state.document?.metadata.person_id) {
      navigate(`/patient/${state.document.metadata.person_id}`);
    }
  };

  const handleNotesClick = () => {
    if (state.documentDetails?.note_id) {
      navigate(`/note/${state.documentDetails.note_id}`);
    }
  };

  // Handle DOCX conversion when document changes
  React.useEffect(() => {
    if (
      state.document &&
      (state.document.content.content_type.includes("wordprocessingml") ||
        state.document.content.content_type.includes(
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ) ||
        state.document.content.filename?.toLowerCase().endsWith(".docx")) &&
      !state.docxHtml &&
      !state.convertingDocx
    ) {
      setState((prev) => ({ ...prev, convertingDocx: true }));
      convertDocxToHtml(state.document.content.content)
        .then((html) => {
          setState((prev) => ({ ...prev, docxHtml: html, convertingDocx: false }));
        })
        .catch((error) => {
          console.error("DOCX conversion failed:", error);
          setState((prev) => ({ ...prev, convertingDocx: false }));
        });
    }
  }, [state.document, state.docxHtml, state.convertingDocx]);

  const convertDocxToHtml = async (base64Content: string): Promise<string> => {
    try {
      // Convert base64 to ArrayBuffer
      const binaryString = atob(base64Content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const arrayBuffer = bytes.buffer;

      // Convert DOCX to HTML using mammoth
      const result = await mammoth.convertToHtml({ arrayBuffer });

      if (result.messages && result.messages.length > 0) {
        console.warn("Mammoth conversion warnings:", result.messages);
      }

      return result.value;
    } catch (error) {
      console.error("Failed to convert DOCX:", error);
      throw new Error("Failed to convert DOCX file");
    }
  };

  const renderDocumentContent = (document: Document) => {
    const { content_type, content } = document.content;

    if (content_type.includes("text")) {
      // Decode base64 content for text files
      try {
        const decodedContent = atob(content);
        return (
          <Box sx={{ p: 3, backgroundColor: "background.surface", borderRadius: "sm" }}>
            <Typography
              component="pre"
              level="body-sm"
              sx={{
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
                lineHeight: 1.5,
              }}
            >
              {decodedContent}
            </Typography>
          </Box>
        );
      } catch (error) {
        console.error("Failed to decode document content:", error);
        return (
          <Card color="danger" variant="soft">
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center">
                <ErrorIcon />
                <Typography>Failed to decode text content</Typography>
              </Stack>
            </CardContent>
          </Card>
        );
      }
    }

    if (content_type.includes("pdf")) {
      // For PDF files, we would typically use an iframe or a PDF viewer library
      return (
        <Box sx={{ width: "100%", height: "calc(100vh - 100px)" }}>
          <iframe
            src={`data:${content_type};base64,${content}`}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="PDF Viewer"
          />
        </Box>
      );
    }

    if (
      content_type.includes("wordprocessingml") ||
      content_type.includes(
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) ||
      document.content.filename?.toLowerCase().endsWith(".docx")
    ) {
      // Handle DOCX files with mammoth.js

      if (state.convertingDocx) {
        return (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
            <Stack spacing={2} alignItems="center">
              <CircularProgress size="lg" />
              <Typography>Converting DOCX document...</Typography>
            </Stack>
          </Box>
        );
      }

      if (state.docxHtml) {
        return (
          <Box
            sx={{
              p: 3,
              backgroundColor: "background.surface",
              borderRadius: "sm",
              "& img": {
                maxWidth: "100%",
                height: "auto",
              },
              "& table": {
                borderCollapse: "collapse",
                width: "100%",
                marginBottom: 2,
              },
              "& th, & td": {
                border: "1px solid #ddd",
                padding: 1,
                textAlign: "left",
              },
              "& th": {
                backgroundColor: "background.level1",
                fontWeight: "bold",
              },
            }}
            dangerouslySetInnerHTML={{ __html: state.docxHtml }}
          />
        );
      }

      // Fallback if conversion failed
      return (
        <Card color="warning" variant="soft">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center">
              <ErrorIcon />
              <Typography>Failed to convert DOCX document</Typography>
            </Stack>
          </CardContent>
        </Card>
      );
    }

    // For other document types (DOC, etc.), we'd need specialized viewers
    return (
      <Card variant="soft" color="warning">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <DescriptionIcon sx={{ fontSize: "3rem" }} />
            <Box>
              <Typography level="title-md">Document Preview Not Available</Typography>
              <Typography level="body-sm">
                This document type cannot be previewed directly in the browser.
              </Typography>
              <Typography level="body-xs" sx={{ mt: 1 }}>
                You can download the document to view it in an appropriate application.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (state.loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size="lg" />
        <Typography>Loading document...</Typography>
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
          p: 4,
        }}
      >
        <ErrorIcon color="error" sx={{ fontSize: "4rem" }} />
        <Typography level="h4" color="danger">
          Error Loading Document
        </Typography>
        <Typography level="body-md" textAlign="center">
          {state.error}
        </Typography>
      </Box>
    );
  }

  if (!state.document) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <DescriptionIcon sx={{ fontSize: "4rem", color: "text.tertiary" }} />
        <Typography level="h4" color="neutral">
          Document Not Found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", overflow: "auto", p: 2 }}>
      <Stack spacing={2}>
        {/* Header with document info and navigation buttons */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Stack spacing={2}>
            <Typography level="h3">{state.document.content.filename}</Typography>
          </Stack>

          {/* Navigation buttons */}
          <Stack direction="row" spacing={1}>
            {state.document?.metadata.person_id && (
              <Button
                variant="soft"
                color="primary"
                startDecorator={
                  state.loadingPatient ? <CircularProgress size="sm" /> : <PersonIcon />
                }
                onClick={handlePatientClick}
                disabled={state.loadingPatient}
              >
                {state.loadingPatient ? "Loading..." : "View Patient"}
              </Button>
            )}
            {(state.documentDetails?.note_id || state.loadingDocumentDetails) && (
              <Button
                variant="soft"
                color="success"
                startDecorator={
                  state.loadingDocumentDetails ? <CircularProgress size="sm" /> : <FactCheckIcon />
                }
                onClick={handleNotesClick}
                disabled={state.loadingDocumentDetails || !state.documentDetails?.note_id}
              >
                {state.loadingDocumentDetails
                  ? "Loading..."
                  : `View Note (${state.documentDetails?.fact_count || 0} facts)`}
              </Button>
            )}
          </Stack>
        </Box>

        {/* Document content */}
        {renderDocumentContent(state.document)}
      </Stack>
    </Box>
  );
};
